import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { makeObservable, observable } from 'mobx';

export interface IDiscountPassengerCountRuleAttributes extends IModelAttributes {
	minCount: number;
	maxCount: number;

	discountId: string;
	discount: Models.DiscountEntity | Models.IDiscountEntityAttributes;

	passengerTypes: Array<
		Models.DiscountPassengerCountRulePassengerType | Models.IDiscountPassengerCountRulePassengerTypeAttributes
	>;
}

/**
 * A single eligibility rule on a Discount: requires the count of passengers
 * whose type is in this rule's PassengerTypes set to fall within
 * [minCount, maxCount] (inclusive) on a given leg.
 * All rules on a Discount must be satisfied (AND).
 * Passenger types must be disjoint across sibling rules on the same Discount.
 */
@entity('DiscountPassengerCountRule')
export default class DiscountPassengerCountRule extends Model implements IDiscountPassengerCountRuleAttributes {
	@observable
	@attribute()
	public minCount: number;

	@observable
	@attribute()
	public maxCount: number;

	@observable
	@attribute()
	public discountId: string;

	@observable
	@attribute({ isReference: true })
	public discount: Models.DiscountEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengerTypes: Models.DiscountPassengerCountRulePassengerType[] = [];

	constructor(attributes?: Partial<IDiscountPassengerCountRuleAttributes>) {
		super(attributes);

		this.passengerTypes = [];

		makeObservable(this);

		if (attributes) {
			if (attributes.minCount !== undefined) {
				this.minCount = attributes.minCount;
			}
			if (attributes.maxCount !== undefined) {
				this.maxCount = attributes.maxCount;
			}
			if (attributes.discountId) {
				this.discountId = attributes.discountId;
			}

			if (attributes.discount) {
				if (attributes.discount instanceof Models.DiscountEntity) {
					this.discount = attributes.discount;
					this.discountId = attributes.discount.id;
				} else {
					this.discount = new Models.DiscountEntity(attributes.discount);
					this.discountId = this.discount.id;
				}
			} else if (attributes.discountId !== undefined) {
				this.discountId = attributes.discountId;
			}

			if (attributes.passengerTypes !== undefined && Array.isArray(attributes.passengerTypes)) {
				for (const model of attributes.passengerTypes) {
					if (model instanceof Models.DiscountPassengerCountRulePassengerType) {
						this.passengerTypes.push(model);
					} else {
						this.passengerTypes.push(new Models.DiscountPassengerCountRulePassengerType(model));
					}
				}
			}
		}
	}
}
